import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Chart from "../../Components/common/Chart/Chart";
import Backdrop from "../../Components/UI/Backdrop/Backdrop";
import GeneralInformationCard from "../../Components/PagesComponents/AnalysisReports/GeneralInformationCard/GeneralInformationCard";
import Classification from "../../Components/PagesComponents/AnalysisReports/Classification/Classification";
import Screenshots from "../../Components/PagesComponents/AnalysisReports/Screenshots/Screenshots";
import ClamCloudMl from "../../Components/PagesComponents/AnalysisReports/ClamCloudMlResults/ClamCloudMl";
import ListPopup from "../../Components/PagesComponents/Dashboard/ListPopup/ListPopup";
import ClassificationTags from "../../Components/PagesComponents/AnalysisReports/Classification/ClassificationTags";
import Content from "./Content/Content";
import PdfIcon from "../../Components/Icon-SVG/PdfIcon";
import { Link } from "react-router-dom";
import {
  RequestGeneralAnalysis,
  RequestGetSignatures,
  RequestNetwork,
  RequestHostsAndDomains,
  RequestScreenshots,
  RequestIocs,
} from "../../store/actions/analysisReportActions";
import { setModalActive, setModalParams } from "../../store/actions/dashboardActions";
import { RequestSearchGroupSubmissions, RequestVms } from "../../store/actions/dashboardActions";
import { loadFromLocalStorage } from "../../utils/localStorage";
import DownloadIcon from "../../Components/Icon-SVG/DownloadIcon";
import useDownloadSample from "../../utils/hook/useDownloadSample";
import DownloadMessage from "../../Components/common/DownloadMessage/DownloadMessage";

import "./styles.scss";
import MLSystemIcon from "../../Components/Icon-SVG/MLSystemIcon";

//GET FILE ID IN URL
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const AnalysisReports = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const theme = loadFromLocalStorage("theme");
  const general = useSelector((state) => state.analysisReports.general);
  const groupSubmissionsLoading = useSelector((state) => state.dashboard.groupSubmissionsLoading);

  const loadingSignatureList = useSelector((state) => state.analysisReports.loadingSignatureList);
  const loadingGeneral = useSelector((state) => state.analysisReports.loadingGeneral);

  const [fileNameHover, setFileNameHover] = useState(false);
  const [staticResultsActive, setStaticResultsActive] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const fileHash = general?.sha1;
  const { downloadOptions, fileName, downloadSampleHandler } = useDownloadSample(fileHash);

  //SET QUERY FOR GETTING DATA ID
  let query = useQuery();

  const clickedFileID = location.state?.clickedFileID;
  const linkDisabled = groupSubmissionsLoading || !clickedFileID;

  //console.log(general);

  const clickHandler = (e) => {
    const fileName = general?.file_name || "-";
    const classification = general?.malwareNames[0] || "-";
    const id = general._id;

    const customPromptSuggestions = [
      "Can you list and explain the malicious activities of " + fileName + " ? <" + id + ">",
      "Why " +
        fileName +
        " report shows us " +
        classification +
        " classification and give me details for " +
        classification +
        " ? <" +
        id +
        ">",
      "Could you list the generated IOCs from " + fileName + " ? <" + id + ">",
      "Can you see any network activity at " +
        fileName +
        " report ? and explain them if there is ? <" +
        id +
        ">",
    ];

    // First set the parameters, then activate the modal
    dispatch(
      setModalParams({
        promptSuggestions: customPromptSuggestions,
        opensession: true,
      }),
    );

    // Now open the modal
    dispatch(setModalActive(true));
  };

  useEffect(() => {
    setShowPopup(false);
    // if (general._id !== query.get("data")) {
    dispatch(
      RequestGeneralAnalysis({
        parentID: query.get("data"),
        clickedFileID: clickedFileID || query.get("data"),
      }),
    );
    dispatch(RequestGetSignatures(query.get("data")));
    dispatch(
      RequestNetwork({
        parentID: query.get("data"),
        clickedFileID: clickedFileID || query.get("data"),
      }),
    );
    dispatch(
      RequestHostsAndDomains({
        parentID: query.get("data"),
        clickedFileID: clickedFileID || query.get("data"),
      }),
    );
    dispatch(
      RequestIocs({
        parentID: query.get("data"),
        clickedFileID: clickedFileID || query.get("data"),
      }),
    );
    dispatch(RequestScreenshots(query.get("data")));
    // }
    if (clickedFileID) {
      dispatch(RequestSearchGroupSubmissions(clickedFileID));
    }
    // Available VMs are needed by the artifact "Submit to Sandbox" popups;
    // on a direct reload only the dashboard page would have fetched them.
    dispatch(RequestVms());
  }, [query.get("data"), clickedFileID]);

  return (
    <div>
      {loadingGeneral && loadingSignatureList ? (
        <Backdrop absolute={true} show={loadingSignatureList} message="Loading report page..." />
      ) : (
        <div className="analysis-reports">
          <div className="analysis-reports__header">
            <div className="analysis-reports__header-doughnutChart">
              {!loadingGeneral && (
                <Chart
                  name={`doughnut-${theme}`}
                  type="doughnut"
                  fileState={query.get("state")}
                  decision_score={general.decision_score}
                />
              )}
            </div>
            <div className="analysis-reports__header-inner">
              <h1>
                <button
                  type="button"
                  className="analysis-reports__header-action"
                  title="Ask AI about this sample"
                  onClick={clickHandler}>
                  <MLSystemIcon color="white" stroke="white" width={22} height={22} />
                </button>
                <Link
                  className="analysis-reports__header-action"
                  title="Export PDF report"
                  to={{
                    pathname: "/report-export",
                    search: `?data=${query.get("data")}&state=${query.get("state")}`,
                    state: { clickedFileID },
                  }}>
                  <PdfIcon />
                </Link>
                <button
                  type="button"
                  className="analysis-reports__header-action"
                  title="Download Sample"
                  onClick={downloadSampleHandler}>
                  <DownloadIcon color="white" stroke="white" width={20} height={20} />
                </button>
                |
                <b
                  style={{ cursor: "default" }}
                  onMouseOver={() => setFileNameHover(false)}
                  onMouseOut={() => setFileNameHover(false)}>
                  {general &&
                    (fileNameHover
                      ? general?.file_name
                      : general?.file_name?.length > 20
                      ? `${general?.file_name?.substring(
                          0,
                          10,
                        )}..........${general?.file_name?.substring(
                          general?.file_name?.length - 6,
                        )}`
                      : general?.file_name)}
                </b>

                <span
                  className={
                    general?.isApt
                      ? "analysis-reports__header-verdict analysis-reports__header-verdict--apt"
                      : query.get("state") === "malicious"
                      ? "analysis-reports__header-verdict analysis-reports__header-verdict--malicious"
                      : query.get("state") === "not suspicious"
                      ? "analysis-reports__header-verdict analysis-reports__header-verdict--not-suspicious"
                      : "analysis-reports__header-verdict analysis-reports__header-verdict--suspicious"
                  }>
                  {general?.isApt ? <span>malicious</span> : query.get("state")}
                </span>
              </h1>
              <p className="analysis-reports__header-explanation">
                You can generate pdf report by clicking the pdf icon.
              </p>
            </div>

            <div style={{ flex: 1, marginLeft: "20px" }}>
              <ClamCloudMl setStaticResultsActive={setStaticResultsActive} />
            </div>
          </div>
          <div className="analysis-reports__body">
            <div className="analysis-reports__body-block">
              <GeneralInformationCard
                key={general}
                fileName={general?.file_name}
                size={general?.size}
                sha1={general?.sha1}
                started={general?.analysis_started}
                reportDate={general?.report_date}
                fileID={query.get("data")}
                linkDisabled={linkDisabled}
                setShowPopup={setShowPopup}
              />
              <Screenshots />
              {theme && <Classification theme={theme} />}
              <ClassificationTags />
            </div>
            <Content
              staticResultsActive={staticResultsActive}
              setStaticResultsActive={setStaticResultsActive}
            />
          </div>
          <Backdrop
            show={downloadOptions.downloadStarted}
            message={
              <DownloadMessage
                fileName={fileName}
                loaded={downloadOptions.loaded}
                total={downloadOptions.total}
                progress={downloadOptions.progress}
              />
            }
          />
        </div>
      )}

      <ListPopup
        show={showPopup}
        setShow={setShowPopup}
        tableType={"analysisReportsPopup"}
        tableType2={"analysisReportsPopup"}
      />
    </div>
  );
};

export default AnalysisReports;
